import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { SaveIntegrationEvents } from '../types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { VcenterWebComponent } from '../../integrations/vcenter';
import { translateFn } from '../../../utils/translator.helper';
import { TemplateState } from '../../../types/common/types';
import { FormGroup } from '../../../ui/gzForm/formGroup/form-group';
import { IntegrationActionContext, IntegrationTypes } from '../../integrationsMarketplaceGridInstalled/types';
import { Subscription } from 'rxjs';
import { IntegrationContainerImageScannerWebComponent } from '../../integrations/containerImageScanner';
import { BaseIntegrationWebComponent } from '../../integrations/BaseIntegrationWebComponent';
import { Company } from '../../integrations/types';

export class SaveIntegrationView extends BaseWebComponentView {
  public currentCompany: Company | undefined;

  public formGroup: FormGroup;

  public dom: {
    $formWrapper: HTMLElement;
    $wrapper: HTMLElement;

    $integrationWebComponent?: BaseIntegrationWebComponent<unknown>;

    $createButton: GzButtonWebComponent;
    $doneButton: GzButtonWebComponent;
    $cancelButton: GzButtonWebComponent;
    $addIntegrationButton: GzButtonWebComponent;
  };

  private formSubscription: Subscription | null = null;

  getDomElements() {
    this.dom = {
      $formWrapper: this.shadow.querySelector('.form-wrapper'),
      $wrapper: this.shadow.querySelector('.wrapper'),
      $createButton: this.shadow.querySelector('#create-button'),
      $doneButton: this.shadow.querySelector('#done-button'),
      $cancelButton: this.shadow.querySelector('#cancel-button'),
      $addIntegrationButton: this.shadow.querySelector('#add-integration-button'),
    };

    this.setLoadingState();

    this.getTranslations();
    this.setTranslatedStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$createButton.innerText = translateFn('Save#preserve state');
    this.dom.$cancelButton.innerText = translateFn('Cancel');
    this.dom.$doneButton.innerText = translateFn('Done');
  }

  async buildSuccessView(viewData: {
    context: IntegrationActionContext | null;
    data: any;
    type: number | null;
  }): Promise<void> {
    this.currentCompany = viewData.data?.company;
    console.log('buildSuccessView saveIntegration2', viewData);

    const type = viewData.data?.type ?? viewData.type;

    // Instantiate and populate the integration web component based on the type
    if (type === IntegrationTypes.Vcenter) {
      this.dom.$integrationWebComponent = new VcenterWebComponent();
    } else if (type === IntegrationTypes.ContainerImageScanner) {
      this.dom.$integrationWebComponent = new IntegrationContainerImageScannerWebComponent();
    } else {
      // default case: Vcenter
      this.dom.$integrationWebComponent = new VcenterWebComponent();
    }

    // Append the integration web component to the form wrapper
    this.dom.$formWrapper.appendChild(this.dom.$integrationWebComponent);

    this.dom.$integrationWebComponent.setAttribute('context', IntegrationActionContext.Create);

    const integration = this.dom.$integrationWebComponent;

    integration.injectDependencies({ ...this.dependencies, createButton: this.dom.$createButton });
    if (viewData.context) {
      integration.formContext = viewData.context;
    }
    integration.setViewData(viewData?.data);
    if (viewData.data?.company) {
      integration.setCompany(viewData.data?.company);
    }

    if (viewData.data?._id) {
      integration.setIntegrationId(viewData.data?._id);
    }

    await integration.start();
    if (viewData.data) {
      integration.setIntegrationData(viewData.data).catch(console.error);
    }

    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(SaveIntegrationEvents.InitSaveIntegrationForm, {
        bubbles: true,
        composed: true,
      })
    );
    this.removeLoadingState();
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  protected onDisconnect() {
    this.formSubscription?.unsubscribe();
  }

  // Instead of createButton ('Save'), we will show doneButton ('Done')
  // when the integration is not a form
  public swapToNonCreateIntegrationMode(nonCreate: boolean): void {
    if (nonCreate) {
      this.dom.$createButton.style.display = 'none';
      this.dom.$doneButton.style.display = 'inline-block';
    } else {
      this.dom.$createButton.style.display = 'inline-block';
      this.dom.$doneButton.style.display = 'none';
    }
  }
}
